import "./style.scss";

import MoreHorizOutlined from "@mui/icons-material/MoreHorizOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Menu, MenuItem } from "@mui/material";
import classNames from "classnames";
import { SmallBody } from "helpers/TypographyComponents";
import type { ReactElement } from "react";
import { useState } from "react";

import StyledButton from "../StyledButton";

export interface Action {
    icon?: ReactElement;
    name: string | ReactElement;
    rightName?: string;
    action?: () => void;
    subtitle?: string;
    disabled?: boolean;
}

interface ButtonMenuProps {
    mainActions: Action;
    actions: Action[];
    buttonType?: "primary" | "outlined" | "secondary";
    expandIcon?: "arrow" | "dots";
    size?: "small" | "medium";
    /**
     * condition to show expand button on the right
     */
    showExpandBtn?: boolean;
    fullWidth?: boolean;
    /**
     * Apply Button style for Lexical Toolbar
     */
    lexicalToolbar?: boolean;
    loading?: boolean;
    disabled?: boolean;
    tooltipText?: string;
    /** Below 1300px, keep only the icon, like ResponsiveButton. */
    responsive?: boolean;
    /** Wrap action subtitles so the menu stays narrow. */
    wrapSubtitles?: boolean;
}

const ButtonMenu = (props: ButtonMenuProps) => {
    const {
        mainActions,
        actions,
        buttonType = "primary",
        expandIcon = "arrow",
        showExpandBtn = true,
        fullWidth = false,
        lexicalToolbar = false,
        loading = false,
        disabled = false,
        tooltipText,
        responsive = false,
        wrapSubtitles = false,
    } = props;
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleMenuExpand = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleMainActionClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (showExpandBtn) mainActions.action && mainActions.action();
        else handleMenuExpand(event);
    };

    return (
        <div className="button-menu" style={fullWidth ? { width: "100%" } : {}}>
            <div
                className={classNames({
                    "with-expand-icon": showExpandBtn,
                    "for-lexical-toolbar": lexicalToolbar,
                    "button-menu-buttons": true,
                })}
            >
                {lexicalToolbar ? (
                    <button
                        onClick={handleMenuExpand}
                        className={"toolbar-item spaced button-menu-main-action "}
                        type="button"
                    >
                        <div className="format strikethrough">
                            {mainActions.icon}
                            {mainActions.name}
                        </div>
                    </button>
                ) : (
                    <StyledButton
                        fullWidth
                        size={props.size}
                        buttonType={buttonType}
                        onClick={handleMainActionClick}
                        loading={loading}
                        disabled={disabled}
                        tooltipText={tooltipText}
                        className="button-menu-main-action"
                        sx={
                            responsive
                                ? {
                                      "@media (max-width: 1299.95px)": {
                                          width: "35px",
                                          minWidth: "35px",
                                          height: "35px",
                                          padding: "6px",
                                          "& .button-menu-main-label": { display: "none" },
                                          "& svg": { position: "absolute" },
                                      },
                                  }
                                : undefined
                        }
                    >
                        {mainActions.icon}
                        <span className="button-menu-main-label">{mainActions.name}</span>
                    </StyledButton>
                )}
                {showExpandBtn && (
                    <StyledButton
                        sx={{ padding: "6.25px !important", width: "16px" }}
                        buttonType={buttonType}
                        onClick={handleMenuExpand}
                        loading={loading}
                        disabled={disabled}
                        tooltipText={tooltipText}
                        className="button-menu-expand"
                    >
                        {expandIcon === "arrow" ? <ExpandMoreIcon /> : <MoreHorizOutlined />}
                    </StyledButton>
                )}
            </div>
            <Menu
                id="menu"
                anchorEl={anchorEl}
                open={open}
                onClose={() => handleClose()}
                className={classNames("button-menu-menu", { "button-menu-menu--wrap": wrapSubtitles })}
                elevation={0}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: fullWidth ? "left" : "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: fullWidth ? "left" : "right",
                }}
                PaperProps={{
                    sx: fullWidth && anchorEl ? { minWidth: anchorEl.offsetWidth } : undefined,
                }}
            >
                {actions.map((act, i) => (
                    <MenuItem
                        key={i}
                        onClick={() => {
                            handleClose();
                            act.action && act.action();
                        }}
                        disabled={act.disabled}
                    >
                        <div className="btn-menu-label-wrapper">
                            <div className="btn-menu-label">
                                <span className="main-label">{act.name}</span>
                                {act.rightName && <span className="description">({act.rightName})</span>}
                            </div>
                            {act.subtitle && <span className="btn-menu-label-description">{act.subtitle}</span>}
                        </div>
                    </MenuItem>
                ))}
            </Menu>
        </div>
    );
};

export default ButtonMenu;
